import { debounce, call, put, select, delay, takeEvery, take } from 'redux-saga/effects'
import { DataDuck } from './DataDuck.typed'
import { getRequest } from '@aytm/app/insights/dashboard/helpers/request'
import { saveCurrentSchema } from '@aytm/app/insights/dashboard/helpers/requests'

import { ERROR_ACTIONS, ErrorDuck } from '../ErrorDuck'

import { createInitiateLoading } from './DataDuck.actionCreators'
import { DATA_ACTIONS } from './DataDuck.actions'
import { splitArray } from '@aytm/app/insights/dashboard/lib/arrays'

// import // tryStoreDataItemsToIDB,
// tryStoreStatItemsToIDB
// tryStoreStatUidToHashResponseToIDB,
// tryStoreUidToHashResponseToIDB
// tryGetDataItemsFromDBByUids
// '@aytm/app/insights/dashboard/duckness/ducks/DataDuck/sagas/storeIntoIDB.saga'
import { getTagsUpdate, tagsFiltersChanged } from '@aytm/app/insights/dashboard/helpers/tags'
import { safelyExecuteSaga } from '@aytm/app/insights/dashboard/lib/safety'
import statQuerySaga from './sagas/statQuery.saga'

const REQUEST_DEBOUNCE_TIMEOUT = 500
const REQUEST_REFETCH_TIMEOUT = 2000
const REQUEST_DATA_BATCH_SIZE = 1000
const REQUEST_STAT_BATCH_SIZE = 100

// sagas

/**
 * Subscribe to GET_SCHEMA action.
 * Generate new request object based on action payload 'params' and current state.
 * Dispatch:
 * - CLEAR_ERROR before request
 * - FETCH_SCHEMA for the first time
 */
DataDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[DATA_ACTIONS.GET_SCHEMA].actionType],
    function* runOnce(action) {
      let errors = yield select(ErrorDuck.select.getErrors)

      if (errors?.schema && errors.schema.length > 0) {
        do {
          yield take(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS].actionType)
          errors = (yield select(ErrorDuck.select.getErrors))['schema']
        } while (errors && errors.length > 0)
      }
      // yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERROR]({ types: ['schema'] }))
      const state = yield select(duckFace.select.state)
      const { params } = action.payload || {}
      const request = getRequest({ ...params, type: 'schema' }, state)
      yield put(duckFace.action[DATA_ACTIONS.FETCH_SCHEMA]({ request, params }))
    }
  )
})

/**
 * Subscribe to FETCH_SCHEMA action.
 * Dispatch:
 * - SET_SCHEMA on complete
 * - SKIP_RESPONSE for invalid blockUID response
 * - WAIT_SCHEMA action on WAIT response status
 * - SET_ERROR on error
 * Run stat query using request body and access key defined in action payload.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.FETCH_SCHEMA].actionType], function* runEvery(action) {
    const { request, params } = action.payload || {}
    const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
    if (error) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'schema', error: error.message }))
      return
    }
    if (!response || !('status' in response)) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'schema', error: 'Invalid schema response' }))
      return
    }
    const { status, blockUID, state: jobState } = response
    if ('OK' === status) {
      const currentBlockUID = yield select(duckFace.select.currentBlockUID)
      yield blockUID === currentBlockUID
        ? put(duckFace.action[DATA_ACTIONS.SET_SCHEMA]({ response, params }))
        : put(duckFace.action[DATA_ACTIONS.SKIP_RESPONSE]({ response }))
    } else if ('WAIT' === status && jobState) {
      yield put(duckFace.action[DATA_ACTIONS.WAIT_SCHEMA]({ response, params }))
    } else {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'schema', error: 'Error fetching schema' }))
    }
  })
})

/**
 * Subscribe to WAIT_SCHEMA action.
 * Dispatch FETCH_SCHEMA action.
 * Wait for REQUEST_REFETCH_TIMEOUT milliseconds and then dispatch FETCH_SCHEMA action again
 * Generate new request object based on response 'queryHash' and 'timestamp' parameters.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.WAIT_SCHEMA].actionType], function* runEvery(action) {
    yield delay(REQUEST_REFETCH_TIMEOUT)
    const { response, params } = action.payload
    const { queryHash, timestamp } = response
    const request = { queryHash, timestamp }
    yield put(duckFace.action[DATA_ACTIONS.FETCH_SCHEMA]({ request, params }))
  })
})

/**
 * Subscribe to SET_SCHEMA action.
 * Dispatch GET_DATA_HASHES action.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.SET_SCHEMA].actionType], function* runEvery(action) {
    yield call(saveCurrentSchema, action)
    const { params } = action.payload
    yield put(duckFace.action[DATA_ACTIONS.GET_DATA_HASHES]({ params }))
  })
})

/**
 * Subscribe to GET_DATA_HASHES action.
 * Dispatch:
 * - CLEAR_ERROR before request
 * - FETCH_DATA_HASHES with new generated request object
 */
DataDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[DATA_ACTIONS.GET_DATA_HASHES].actionType],
    function* runOnce(action) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['dataHashes'] }))
      const state = yield select(duckFace.select.state)
      const { params } = action.payload || {}
      const request = getRequest({ ...params, type: 'dataHashes' }, state)
      yield put(duckFace.action[DATA_ACTIONS.FETCH_DATA_HASHES]({ request, params }))
    }
  )
})

/**
 * Subscribe to FETCH_DATA_HASHES action.
 * Dispatch:
 * - SET_DATA_HASHES on complete
 * - SKIP_RESPONSE for invalid blockUID response
 * - WAIT_DATA_HASHES action on WAIT response status
 * - SET_ERROR on error
 * Run stat query using request body and access key defined in action payload.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.FETCH_DATA_HASHES].actionType], function* runEvery(action) {
    const { request, params } = action.payload || {}
    const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
    if (error) {
      yield put(
        ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
          type: 'dataHashes',
          error: 'Error fetching data hashes.' + (error.message ? ' Reason: ' + error.message : '')
        })
      )
      return
    }
    if (!response || !('status' in response)) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'schema', error: 'Invalid schema response' }))
      return
    }
    const { status, blockUID, state: jobState } = response
    if ('OK' === status) {
      const currentBlockUID = yield select(duckFace.select.currentBlockUID)
      yield blockUID === currentBlockUID
        ? put(duckFace.action[DATA_ACTIONS.SET_DATA_HASHES]({ response, params }))
        : put(duckFace.action[DATA_ACTIONS.SKIP_RESPONSE]({ response }))
    } else if ('WAIT' === status && jobState) {
      yield put(duckFace.action[DATA_ACTIONS.WAIT_DATA_HASHES]({ response, params }))
    } else {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'dataHashes', error: 'Error fetching data hashes' }))
    }
  })
})

/**
 * Subscribe to WAIT_DATA_HASHES action.
 * Dispatch FETCH_DATA_HASHES action.
 * Wait for REQUEST_REFETCH_TIMEOUT milliseconds and then dispatch GET_DATA_HASHES action again
 * to fetch requested data using queryHash returned by previous request contained
 * in action payload.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.WAIT_DATA_HASHES].actionType], function* runEvery(action) {
    yield delay(REQUEST_REFETCH_TIMEOUT)
    const { response, params } = action.payload
    const { queryHash, timestamp } = response
    const request = { queryHash, timestamp }
    yield put(duckFace.action[DATA_ACTIONS.FETCH_DATA_HASHES]({ request, params }))
  })
})

/**
 * Subscribe to SET_DATA_HASHES action.
 * Dispatch GET_DATA_ITEMS action.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.SET_DATA_HASHES].actionType], function* runEvery(action) {
    const { params, response: _ } = action.payload
    yield put(duckFace.action[DATA_ACTIONS.GET_DATA_ITEMS]({ params }))
    // yield spawn(tryStoreUidToHashResponseToIDB, response)
  })
})

/**
 * Subscribe to GET_DATA_ITEMS action.
 * Dispatch:
 * - CLEAR_ERROR before request
 * - FETCH_DATA_ITEMS for the first time
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.GET_DATA_ITEMS].actionType], function* runOnce(action) {
    /// DATA_ACTIONS.GET_DATA_ITEMS
    const { params } = action.payload || {}
    yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['dataItems'] }))
    yield put(duckFace.action[DATA_ACTIONS.FETCH_DATA_ITEMS]({ params }))
  })
})

/**
 * Subscribe to FETCH_DATA_ITEMS action.
 * Dispatch:
 * - FETCH_DATA_ITEMS if hashes are available
 * - SET_DATA_ITEMS on success result
 * - SKIP_RESPONSE for invalid blockUID response
 * - SET_ERROR on error
 * - GET_DATA_ITEMS if there are no hashes to process
 * Run stat query using request generated from last state
 */

DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.FETCH_DATA_ITEMS].actionType], function* runEvery(action) {
    const { params } = action.payload || {}
    const data = yield select(duckFace.select.data)
    // We take all uid-hash pairs from data, then try to get data items from IndexedDB by uids.
    // If some items are found, we dispatch SET_DATA_ITEMS action to store them in Redux store.
    // For the remaining hashes, we proceed to fetch them from the server.
    const [uidHashesPairs] = splitArray(
      Array.from(Object.entries(data || {})),
      ([, v]) => 'string' === typeof v && 'empty' !== v
    )

    const hashesToFetch = uidHashesPairs.map(([, hash]) => hash)
    // Alternatively, we can try to get data items from IndexedDB here. Uncomment the following lines to enable this behavior.
    // const [dataItems, hashesToFetch] = yield call(tryGetDataItemsFromDBByUids, uidHashesPairs)

    // if (Object.keys(dataItems).length) {
    //   const response = { dataItems: { items: dataItems } }
    //   const uidHashes = Object.fromEntries(uidHashesPairs)
    //   const hashes = Object.keys(dataItems).map(uid => uidHashes[uid])
    //   yield put(duckFace.action[DATA_ACTIONS.SET_DATA_ITEMS]({ response, params, hashes }))
    //   const processedData = yield select(duckFace.select.data)
    //   logged(processedData, 'processedData after SET_DATA_ITEMS')
    //   debugger
    // }

    const hashes = hashesToFetch.slice(0, REQUEST_DATA_BATCH_SIZE)

    if (hashes.length) {
      const state = yield select(duckFace.select.state)
      const request = getRequest({ hashes, type: 'dataItems' }, state)
      const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
      if (error) {
        yield put(
          ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
            type: 'dataItems',
            error: 'Error fetching data items.' + (error.message ? ' Reason: ' + error.message : '')
          })
        )
        return
      }
      const { status, blockUID, dataItems: _ } = response
      if ('OK' === status) {
        const currentBlockUID = yield select(duckFace.select.currentBlockUID)
        if (blockUID === currentBlockUID) {
          yield put(duckFace.action[DATA_ACTIONS.SET_DATA_ITEMS]({ response, params, hashes }))
          // const processedData = yield select(duckFace.select.data)
          // yield spawn(tryStoreDataItemsToIDB, dataItems?.items, processedData, hashes)
          yield put(duckFace.action[DATA_ACTIONS.FETCH_DATA_ITEMS]({ params }))
        } else {
          yield put(duckFace.action[DATA_ACTIONS.SKIP_RESPONSE]({ response }))
        }
      } else {
        yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'dataItems', error: 'Error fetching data items' }))
      }
    } else {
      yield put(duckFace.action[DATA_ACTIONS.SET_DATA_ITEMS]({ response: {}, params, hashes: [] }))
      yield put(duckFace.action[DATA_ACTIONS.GET_STAT_HASHES]({ params }))
    }
  })
})

/**
 * Subscribe to GET_STAT_HASHES action.
 * Dispatch:
 * - CLEAR_ERROR before request
 * - FETCH_STAT_HASHES with new generated request object
 */
DataDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[DATA_ACTIONS.GET_STAT_HASHES].actionType],
    function* runOnce(action) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['statHashes'] }))
      const state = yield select(duckFace.select.state)
      const data = yield select(duckFace.select.data)
      const { params } = action.payload || {}
      const hashes = Object.values(data || {})
        .filter(v => 'object' === typeof v && v.hash)
        .map(v => v.hash)
      const request = getRequest({ ...params, hashes, type: 'statHashes' }, state)
      yield put(duckFace.action[DATA_ACTIONS.FETCH_STAT_HASHES]({ request, params }))
    }
  )
})

/**
 * Subscribe to FETCH_STAT_HASHES action.
 * Dispatch:
 * - SET_STAT_HASHES on complete
 * - SKIP_RESPONSE for invalid blockUID response
 * - WAIT_STAT_HASHES action on WAIT response status
 * - SET_ERROR on error
 * Run stat query using request body and access key defined in action payload.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.FETCH_STAT_HASHES].actionType], function* runEvery(action) {
    const { request, params } = action.payload || {}
    const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
    if (error) {
      yield put(
        ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
          type: 'statHashes',
          error: 'Error fetching stat hashes.' + (error.message ? ' Reason: ' + error.message : '')
        })
      )
      return
    }
    if (!response || !('status' in response)) {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'schema', error: 'Invalid schema response' }))
      return
    }
    const { status, blockUID, state: jobState } = response
    if ('OK' === status) {
      const currentBlockUID = yield select(duckFace.select.currentBlockUID)
      yield blockUID === currentBlockUID
        ? put(duckFace.action[DATA_ACTIONS.SET_STAT_HASHES]({ response, params }))
        : put(duckFace.action[DATA_ACTIONS.SKIP_RESPONSE]({ response }))
    } else if ('WAIT' === status && jobState) {
      yield put(duckFace.action[DATA_ACTIONS.WAIT_STAT_HASHES]({ response, params }))
    } else {
      yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'statHashes', error: 'Error fetching stat hashes' }))
    }
  })
})

/**
 * Subscribe to WAIT_STAT_HASHES action.
 * Dispatch FETCH_STAT_HASHES action.
 * Wait for REQUEST_REFETCH_TIMEOUT milliseconds and then dispatch FETCH_STAT_HASHES action again
 * to fetch requested data using queryHash returned by previous request contained
 * in action payload.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.WAIT_STAT_HASHES].actionType], function* runEvery(action) {
    yield delay(REQUEST_REFETCH_TIMEOUT)
    const { response, params } = action.payload
    const { queryHash, timestamp } = response
    const request = { queryHash, timestamp }
    yield put(duckFace.action[DATA_ACTIONS.FETCH_STAT_HASHES]({ request, params }))
  })
})

/**
 * After the app receives stat hashes and stores them in store,
 * it starts retrieving the items by hashes
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.SET_STAT_HASHES].actionType], function* runEvery(action) {
    const { params, response: _ } = action.payload
    yield put(duckFace.action[DATA_ACTIONS.GET_STAT_ITEMS]({ params }))
    // yield call(tryStoreStatUidToHashResponseToIDB, response)
  })
})

/**
 * Subscribe to GET_STAT_ITEMS action.
 * Dispatch:
 * - CLEAR_ERROR before request
 * - FETCH_STAT_ITEMS with new generated request object
 */
DataDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[DATA_ACTIONS.GET_STAT_ITEMS].actionType],
    function* runOnce(action) {
      const { params } = action.payload || {}
      yield put(ErrorDuck.action[ERROR_ACTIONS.CLEAR_ERRORS]({ types: ['statItems'] }))
      yield put(duckFace.action[DATA_ACTIONS.FETCH_STAT_ITEMS]({ params }))
    }
  )
})

/**
 * Subscribe to FETCH_STAT_ITEMS action.
 * Dispatch:
 * - FETCH_STAT_ITEMS if hashes are available
 * - SET_STAT_ITEMS on complete
 * - SKIP_RESPONSE for invalid blockUID response
 * - SET_ERROR on error
 * - END_REQUEST_CHAIN if there are no hashes to process
 * Run stat query using request body and access key defined in action payload.
 */
DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.FETCH_STAT_ITEMS].actionType], function* runEvery(action) {
    const { params } = action.payload || {}
    const showSigtest = yield select(duckFace.select.showSigtest)
    if (!showSigtest) {
      // Skip fetching stat items of show sigtest is off
      yield put(duckFace.action[DATA_ACTIONS.END_REQUEST_CHAIN]({ params }))
      return
    }
    const stat = yield select(duckFace.select.stat)
    const hashes = Object.values(stat || {})
      .filter(v => 'string' === typeof v && 'empty' !== v)
      .slice(0, REQUEST_STAT_BATCH_SIZE)

    if (hashes.length) {
      const state = yield select(duckFace.select.state)
      const request = getRequest({ hashes, type: 'statItems' }, state)
      const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
      if (error) {
        yield put(
          ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
            type: 'statItems',
            error: 'Error fetching stat items data.' + (error.message ? ' Reason: ' + error.message : '')
          })
        )
        return
      }
      const { status, blockUID, statItems: _ } = response
      if ('OK' === status) {
        // yield spawn(tryStoreStatItemsToIDB, statItems?.items, hashes)
        const currentBlockUID = yield select(duckFace.select.currentBlockUID)
        if (blockUID === currentBlockUID) {
          yield put(duckFace.action[DATA_ACTIONS.SET_STAT_ITEMS]({ response, params, hashes }))
          // yield spawn(tryStoreStatItemsToIDB, statItems?.items, hashes)
          yield put(duckFace.action[DATA_ACTIONS.FETCH_STAT_ITEMS]({ params }))
        } else {
          yield put(duckFace.action[DATA_ACTIONS.SKIP_RESPONSE]({ response }))
        }
      } else {
        yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'statItems', error: 'Error fetching stat items' }))
      }
    } else {
      yield put(duckFace.action[DATA_ACTIONS.END_REQUEST_CHAIN]({ params }))
    }
  })
})

DataDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[DATA_ACTIONS.SUBJECT_UPDATE].actionType],
    function* runOnce() {
      const state = yield select(duckFace.select.state)
      const request = getRequest({ type: 'update' }, state)
      const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
      if (error) {
        yield put(
          ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
            type: 'update',
            error: 'Error updating subject.' + (error.message ? ' Reason: ' + error.message : '')
          })
        )
        return
      }
      const { status, update } = response
      // eslint-disable-next-line unicorn/prefer-ternary
      if ('OK' === status) {
        yield put(duckFace.action[DATA_ACTIONS.SUBJECT_UPDATE_SET](update))
      } else {
        yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'update', error: 'Error updating subject' }))
      }
    }
  )
})

DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.SUBJECT_UPDATE_SET].actionType], function* runEvery(action) {
    yield call(saveCurrentSchema, action)
    yield put(createInitiateLoading())
  })
})

DataDuck.saga(function* run(duckFace) {
  yield debounce(
    REQUEST_DEBOUNCE_TIMEOUT,
    [duckFace.action[DATA_ACTIONS.TAGS_UPDATE].actionType],
    function* runOnce(action) {
      const state = yield select(duckFace.select.state)
      const { action: updateAction, uid, tags, deleted } = action.payload || {}
      const refreshNeeded = tagsFiltersChanged(state, { uid, tags, deleted })
      const update = getTagsUpdate(state, { action: updateAction, uid, tags })
      const request = getRequest({ type: 'update', update }, state)
      const [error, response] = yield call(safelyExecuteSaga, statQuerySaga, request)
      if (error) {
        yield put(
          ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({
            type: 'updateTags',
            error: 'Error updating tags.' + (error.message ? ' Reason: ' + error.message : '')
          })
        )
        return
      }
      const { status } = response
      if ('OK' === status) {
        yield put(duckFace.action[DATA_ACTIONS.TAGS_UPDATE_SET]({ uid, tags, deleted }))
        if (refreshNeeded) {
          yield put(createInitiateLoading())
        }
      } else {
        yield put(ErrorDuck.action[ERROR_ACTIONS.SET_ERROR]({ type: 'updateTags', error: 'Error updating tags' }))
      }
    }
  )
})

DataDuck.saga(function* run(duckFace) {
  yield takeEvery([duckFace.action[DATA_ACTIONS.END_REQUEST_CHAIN].actionType], function* runEvery(action) {
    const { params } = action.payload || {}
    if (params?.dispatchAfter) {
      yield put(params.dispatchAfter())
    }
  })
})

export default DataDuck
